<template>
  <section class="features lp-section">
    <div class="lp-container">
      <!-- Section header -->
      <header class="features-head">
        <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ $t('nav.features') }}</div>
        <h2 class="lp-h2 lp-balance">
          {{ $t('features.title1') }}
          <span class="lp-accent-text">{{ $t('features.titleHighlight') }}</span>
        </h2>
        <p class="lp-lead features-sub lp-balance">{{ $t('features.subtitle') }}</p>
      </header>

      <!-- Alternating benefit rows -->
      <div class="feature-rows">
        <article
          v-for="(feature, i) in mainFeatures"
          :key="i"
          class="feature-row"
          :class="{ 'feature-row--reverse': i % 2 === 1 }"
        >
          <div class="feature-copy">
            <div class="feature-icon">
              <l-icon size="22" color="primary">{{ feature.icon }}</l-icon>
            </div>
            <h3 class="lp-h3 feature-title">{{ feature.title }}</h3>
            <p class="feature-desc">{{ feature.desc }}</p>
            <ul class="feature-list">
              <li v-for="(h, j) in feature.highlights" :key="j">
                <l-icon size="17" color="primary">mdi-check</l-icon>{{ h }}
              </li>
            </ul>
          </div>
          <div class="feature-visual">
            <div class="lp-frame feature-frame">
              <app-screenshot :variant="feature.variant" :label="feature.title"></app-screenshot>
            </div>
          </div>
        </article>
      </div>

      <!-- Secondary capabilities -->
      <div class="more-grid">
        <div v-for="(m, i) in moreFeatures" :key="i" class="more-card">
          <div class="more-icon">
            <l-icon size="20" color="primary">{{ m.icon }}</l-icon>
          </div>
          <h4 class="more-title">{{ m.title }}</h4>
          <p class="more-desc">{{ m.desc }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import AppScreenshot from '@/components/mockup/AppScreenshot.vue';

export default {
  name: 'FeaturesSection',
  components: { AppScreenshot },
  computed: {
    mainFeatures() {
      const items = this.$t('features.main');
      // Iconos y mockup emparejados por posición con features.main del i18n:
      // respuestas sugeridas · edición rápida · conexiones · seguimientos ·
      // colaboradores · multiplataforma.
      const icons = ['sparkles', 'pen-line', 'plug', 'flag', 'users', 'monitor'];
      const variants = ['replies', 'edit', 'tools', 'followups', 'collab', 'platforms'];
      return items.map((f, i) => ({
        ...f,
        highlights: f.h,
        icon: icons[i] || 'sparkles',
        variant: variants[i] || 'inbox',
      }));
    },
    moreFeatures() {
      const items = this.$t('features.more');
      // Seguimientos ya no está aquí: tiene fila propia arriba.
      const icons = [
        'user-cog', 'filter', 'scan-search', 'zap',
        'bell', 'folder-tree', 'shield',
        'calendar-clock', 'file-text', 'paperclip', 'check-check',
      ];
      return items.map((f, i) => ({ ...f, icon: icons[i] || 'sparkles' }));
    },
  },
};
</script>

<style scoped>
.features { background: var(--lp-bg); }

.features-head {
  max-width: 680px;
  margin: 0 auto 88px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}
.features-sub { margin-top: 2px; }

.feature-rows {
  display: flex;
  flex-direction: column;
  gap: 120px;
}

.feature-row {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  align-items: center;
  gap: 72px;
}
.feature-row--reverse .feature-copy { order: 2; }
.feature-row--reverse .feature-visual { order: 1; }

.feature-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lp-accent-weak);
  margin-bottom: 22px;
}
.feature-title { margin-bottom: 16px; }
.feature-desc {
  font-size: 1.0625rem;
  line-height: 1.6;
  color: var(--lp-muted);
  margin: 0 0 24px;
}
.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.feature-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.975rem;
  font-weight: 500;
  color: var(--lp-ink-2);
}

/* min-width:0 evita que el lienzo de ancho fijo del mockup desborde la columna
   del grid (min-width:auto) y le impida escalar en pantallas estrechas. */
.feature-copy { min-width: 0; }
.feature-visual { min-width: 0; }
.feature-frame { width: 100%; min-width: 0; overflow: hidden; }

/* Secondary grid */
.more-grid {
  margin-top: 120px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}
.more-card {
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  padding: 26px 24px;
  box-shadow: var(--lp-shadow-sm);
  transition: border-color 0.2s ease, transform 0.2s ease;
}
.more-card:hover {
  border-color: var(--lp-line-strong);
  transform: translateY(-3px);
}
.more-icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lp-accent-weak);
  margin-bottom: 18px;
}
.more-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--lp-ink);
  margin: 0 0 8px;
}
.more-desc {
  font-size: 0.925rem;
  line-height: 1.55;
  color: var(--lp-muted);
  margin: 0;
}

@media (max-width: 959px) {
  .feature-rows { gap: 80px; }
  .feature-row {
    grid-template-columns: 1fr;
    gap: 36px;
  }
  .feature-row--reverse .feature-copy { order: 1; }
  .feature-row--reverse .feature-visual { order: 2; }
  .features-head { margin-bottom: 64px; }
  .more-grid {
    grid-template-columns: repeat(2, 1fr);
    margin-top: 80px;
  }
}
@media (max-width: 599px) {
  .more-grid { grid-template-columns: 1fr; gap: 28px; }
}
</style>
